import { useEffect, useState } from 'react'
import type { Discipline } from '@structural-climbing/validation'

/**
 * Members see a route's average only once this many climbers have rated it.
 * Mirrors MIN_RATINGS_TO_SHOW in workers/api/src/handlers/climbingRoutes.ts,
 * which does the hiding; this copy only feeds the "2 of 5" note.
 */
export const MIN_RATINGS_TO_SHOW = 5

export interface ClimberPreview {
  userId: string
  nickname: string | null
  name: string | null
  avatarUrl: string | null
}

export interface GymRoute {
  id: string
  grade: number
  color: string | null
  image_r2_key: string | null
  created_at: string
  setter_note: string | null
  /** Who set it. `name` is their nickname, else their full name, else null. */
  setter?: { id: string; name: string | null } | null
  has_ascent: boolean
  attempts: number | null
  ascent_count: number
  /** The signed-in member's own rating, 1-5, or null if they have not rated it. */
  my_rating: number | null
  /**
   * Average of every rating on this route. The Worker sends null until
   * MIN_RATINGS_TO_SHOW climbers have rated it, so one early vote cannot decide it.
   */
  avg_rating: number | null
  rating_count: number
  top_climbers: ClimberPreview[]
  private_count: number
  section: { id: string; name: string; discipline: string | null } | null
}

export function useGymRoutes(
  gymId: string | null,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
  status: 'active' | 'archived' = 'active',
  // The server answers one discipline at a time and assumes bouldering.
  discipline: Discipline = 'boulder',
) {
  const [routes, setRoutes] = useState<GymRoute[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!gymId) {
      setRoutes([])
      setTotal(0)
      return
    }
    const resolvedGymId = gymId
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(
          `${apiUrl}/my-gym-routes?status=${status}&gym_id=${encodeURIComponent(resolvedGymId)}&discipline=${discipline}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        if (res.ok && !cancelled) {
          const body = (await res.json()) as { routes: GymRoute[]; total?: number } | GymRoute[]
          if (Array.isArray(body)) {
            setRoutes(body)
            setTotal(body.length)
          } else {
            setRoutes(body.routes)
            setTotal(body.total ?? body.routes.length)
          }
        }
      } catch {
        // silently ignore — routes are an enhancement
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [gymId, apiUrl, getAccessTokenSilently, status, discipline])

  return { routes, total, loading, setRoutes }
}
